import { DailySchedule, SearchParameters, SearchParametersDTO } from '../models';
import { apiClient } from './ApiClient';

// Types for the cached data and responses
export interface MonthData {
  dailySchedulesByDate: Record<string, DailySchedule>;
  optimizedScheduleIndexes: Record<string, number[]>; // Changed to store indexes instead of duplicating data
  locationDetails: {
    name: string;
    region: string;
    country: string;
    coordinates: { latitude: number; longitude: number };
  };
  tideHeightStatistics?: {
    minHeight: number;
    maxHeight: number;
  };
}

// The cache is completely internal to this service
class TideDataService {
  private cache: Record<string, MonthData> = {};

  // Private method to generate cache keys
  /**
   * Creates a cache key for storing/retrieving month data
   * Uses location ID, year-month, and services hash
   */
  private createCacheKey(searchParams: SearchParameters): string {
    const { location, dateOfInterest, services } = searchParams;
    const year = dateOfInterest.getFullYear();
    const month = dateOfInterest.getMonth(); // 0-indexed
    const yearMonth = `${year}-${month + 1}`;
    const servicesHash = JSON.stringify(services.map(s => ({ id: s.id, name: s.name })));
    return `${location.id}_${yearMonth}_${servicesHash}`;
  }

  // Public method to get month data using the new SearchParameters model
  async getMonthData(
    searchParams: SearchParameters,
    forceRefresh: boolean = false
  ): Promise<MonthData> {
    const { location, dateOfInterest, services } = searchParams;
    const year = dateOfInterest.getFullYear();
    const month = dateOfInterest.getMonth(); // 0-indexed
    const cacheKey = this.createCacheKey(searchParams);
    
    // Return from cache if available (unless force refresh)
    if (!forceRefresh && this.cache[cacheKey]) {
      console.log(`Using cached data for ${year}-${month+1}`);
      return this.cache[cacheKey];
    }
    
    console.log(`Fetching new data for ${year}-${month+1}`);
    
    try {
      
      console.log(`TideDataService.getMonthData: Fetching for year=${year}, month=${month}`);

      // Convert to DTO format for API call
      const searchParamsDTO: SearchParametersDTO = {
        locationId: location.id,
        dateOfInterest,
        services
      };
      
      // Use the new monthly endpoint
      // Note: Need to update ApiClient to accept DTO, using any for now to avoid type errors
      const monthlyResult = await apiClient.getMonthlyServiceOpportunities(searchParamsDTO as any);

      console.log(`TideDataService: API request sent with month=${month + 1}`);
    
      // Use location data from the SearchParameters object
      const locationDetailsData = {
        name: location.name,
        region: location.region || "New South Wales",
        country: location.country || "Australia",
        coordinates: location.coordinates
      };
      
      // Convert the monthly schedules to the expected format
      const dailySchedulesByDate: Record<string, DailySchedule> = {};
      
      // Process regular schedules and optimized schedule indexes from backend
      // Now the schedules are in arrays where index is day of month (0 is unused)
      const optimizedIndexesByDate: Record<string, number[]> = {};
      
      for (let dayIndex = 1; dayIndex < monthlyResult.dailySchedules.length; dayIndex++) {
        const schedule = monthlyResult.dailySchedules[dayIndex];
        if (!schedule) continue; // Skip if no schedule for this day
        
        // Manually construct the date string to avoid timezone issues
        const dateString = `${year}-${String(month + 1).padStart(2, '0')}-${String(dayIndex).padStart(2, '0')}`;
        
        // Store regular schedule
        dailySchedulesByDate[dateString] = schedule;
        
        // Get the optimized schedule indexes for this day
        const optimizedSchedule = monthlyResult.optimizedDailySchedules[dayIndex];
        if (optimizedSchedule && optimizedSchedule.optimizedScheduleIndexes) {
          optimizedIndexesByDate[dateString] = optimizedSchedule.optimizedScheduleIndexes;
        }
      }
      
      console.log("Received monthly data:", monthlyResult);
      
      // Check for astronomical data
      for (let dayIndex = 1; dayIndex < monthlyResult.dailySchedules.length; dayIndex++) {
        const schedule = monthlyResult.dailySchedules[dayIndex];
        if (schedule && schedule.tideData && schedule.tideData.astronomicalData) {
          console.log(`Day ${dayIndex} has astronomical data:`, schedule.tideData.astronomicalData);
          // Just log the first day with astronomical data
          break;
        }
      }
      
      // Create the data object with regular schedules, optimized indexes, and tide height statistics
      const monthData: MonthData = {
        dailySchedulesByDate: dailySchedulesByDate,
        optimizedScheduleIndexes: optimizedIndexesByDate,
        locationDetails: locationDetailsData,
        tideHeightStatistics: monthlyResult.tideHeightStatistics
      };
      
      // Cache the data
      this.cache[cacheKey] = monthData;
      
      return monthData;
    } catch (error) {
      console.error(`Error fetching data for ${year}-${month+1}:`, error);
      
      // Provide a fallback empty data structure instead of failing
      const emptyMonthData: MonthData = {
        dailySchedulesByDate: {},
        optimizedScheduleIndexes: {},
        locationDetails: {
          name: location.id.toString(),
          region: "Unknown",
          country: "Unknown",
          coordinates: { latitude: 0, longitude: 0 }
        }
      };
      
      // Don't cache failed requests
      return emptyMonthData;
    }
  }

  // Invalidate cache for specific parameters - updated to use SearchParameters
  clearCachedData(searchParams: SearchParameters): void {
    const cacheKey = this.createCacheKey(searchParams);
    if (this.cache[cacheKey]) {
      delete this.cache[cacheKey];
    }
  }

  // Clear all cached data
  clearAllCachedData(): void {
    this.cache = {};
  }
}

// Create singleton instance
export const tideDataService = new TideDataService();
